import type { SourceRef } from './brief'
import { memberArticleUrl } from '../lab/memberLink'

/** One archive-search hit: a member article or a magazine issue of this title. */
export type ArchiveHit = { kind: 'member' | 'issue'; id: number; title: string | null; label: string | null }

/** A ref with the title the editor saw when choosing it. The title is for
 *  display only; the brief and the PATCH carry the bare ref. */
export type TitledRef = { ref: SourceRef; title: string }

export const refKey = (r: SourceRef): string => ('url' in r ? `web:${r.url}` : `${r.kind}:${r.id}`)

const KIND_WORD: Record<SourceRef['kind'], string> = { member: 'Member article', issue: 'Issue', public: 'Public post', web: 'Web' }

/** What a chip says: the remembered title, else something that still names the ref
 *  (a redraft's pins arrive from the old brief without titles). */
export function refTitle(r: SourceRef, titles: Record<string, string>): string {
  const t = titles[refKey(r)]
  if (t) return t
  return 'url' in r ? r.url : `${KIND_WORD[r.kind]} ${r.id}`
}

export const hitRef = (h: ArchiveHit): SourceRef => ({ kind: h.kind, id: h.id })
export const hitTitle = (h: ArchiveHit): string => h.title || `${KIND_WORD[h.kind]} ${h.id}`

export function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n).trimEnd()}…` : s
}

/** The archive search runs for 2–100 characters (the API answers 422 otherwise). */
export function searchTerm(raw: string): string | null {
  const q = raw.trim()
  return q.length >= 2 && q.length <= 100 ? q : null
}

/** Add a hit to the review panel's pending additions, once. */
export function addPending(pending: TitledRef[], h: ArchiveHit): TitledRef[] {
  const ref = hitRef(h)
  return pending.some((p) => refKey(p.ref) === refKey(ref)) ? pending : [...pending, { ref, title: hitTitle(h) }]
}

/** The PATCH body for the review panel. */
export function sourceEdit(remove: Iterable<string>, pending: TitledRef[]): { remove: string[]; include: { kind: 'member' | 'issue'; id: number }[] } {
  return {
    remove: [...remove],
    include: pending.flatMap((p) => (p.ref.kind === 'member' || p.ref.kind === 'issue') && 'id' in p.ref ? [{ kind: p.ref.kind, id: p.ref.id }] : []),
  }
}

export function safeHref(u: string | null | undefined): string | undefined {
  if (!u) return undefined
  return /^https?:\/\//i.test(u) ? u : undefined
}

/** True when an archive hit is already in the review list: a draft source the
 *  editor has not ticked for removal (ids are `M<article id>` / `I<edition id>`),
 *  or a pending addition. */
export function alreadyListed(h: ArchiveHit, sourceIds: Iterable<string>, removed: ReadonlySet<string>, pending: TitledRef[]): boolean {
  const sid = `${h.kind === 'member' ? 'M' : 'I'}${h.id}`
  for (const id of sourceIds) if (id === sid && !removed.has(id)) return true
  return pending.some((p) => refKey(p.ref) === refKey(hitRef(h)))
}

/** Where a source row links: web sources to their URL, member articles to the
 *  member site by slug (`base` is the title's public origin from
 *  usePublicBaseUrls), anything else nowhere. */
export function sourceHref(base: string | undefined, s: { kind: string; url?: string | null; slug?: string | null }): string | undefined {
  if (s.kind === 'web') return safeHref(s.url)
  if (s.kind === 'member') return memberArticleUrl(base, s.slug) ?? undefined
  return undefined
}

export const WRITE_UNLOCK_MS = 90_000

/** Whether Write it should stay locked after a successful POST at `postedAt`
 *  (ms): until a run is seen finishing after the POST, or, if nothing is
 *  running, until WRITE_UNLOCK_MS have passed (a run that failed between polls). */
export function writeStillPending(
  postedAt: number | null,
  status: { running: boolean; last?: { finished_at: string } | null } | undefined,
  now: number,
): boolean {
  if (postedAt === null) return false
  if (status?.running) return true
  const fin = status?.last?.finished_at ? Date.parse(status.last.finished_at) : NaN
  if (fin > postedAt) return false
  return now - postedAt < WRITE_UNLOCK_MS
}

/** Save changes is off with nothing to save, while saving, and while a Write it is starting, pending or
 *  the title is running — the writer is reading this bundle then. */
export function canSaveReview(o: { dirty: boolean; saving: boolean; running: boolean; started: boolean; writing: boolean }): boolean {
  return o.dirty && !o.saving && !o.running && !o.started && !o.writing
}
